import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Package,
  CircleNotch,
  CheckCircle,
  Upload,
  Download,
  FileArrowUp,
  XCircle,
  Sliders
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AddStock() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const initialTab = searchParams.get('tab') || 'add';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products`, { withCredentials: true });
        setProducts(res.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="max-w-2xl mx-auto animate-slide-up" data-testid="add-stock-page">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-black/40 mx-auto mb-4 flex items-center justify-center rounded-2xl">
          <Package size={32} className="text-[var(--primary)]" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>
          {t('addStockTitle')}
        </h2>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[var(--bg)] border border-[var(--dm-border)] p-1 w-full grid grid-cols-3" data-testid="stock-tabs">
          <TabsTrigger value="add" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-add-stock">
            <Package size={16} className="mr-2" />
            {t('addStockTitle')}
          </TabsTrigger>
          <TabsTrigger value="upload" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-upload-stock">
            <Upload size={16} className="mr-2" />
            {t('uploadStock')}
          </TabsTrigger>
          <TabsTrigger value="adjust" className="data-[state=active]:bg-black data-[state=active]:text-[var(--text)] rounded-lg" data-testid="tab-adjust-stock">
            <Sliders size={16} className="mr-2" />
            {t('adjustStock')}
          </TabsTrigger>
        </TabsList>

        {/* Add Stock Tab */}
        <TabsContent value="add" className="mt-4">
          <AddStockForm products={products} loading={loading} navigate={navigate} t={t} />
        </TabsContent>

        {/* Upload Stock Tab */}
        <TabsContent value="upload" className="mt-4">
          <UploadStockForm navigate={navigate} t={t} />
        </TabsContent>

        {/* Adjust Stock Tab */}
        <TabsContent value="adjust" className="mt-4">
          <AdjustStockForm products={products} loading={loading} navigate={navigate} t={t} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AddStockForm({ products, loading, navigate, t }) {
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '', quantity: '', cost_price: '', tax_paid: '', supplier_name: '', reference_number: '', notes: ''
  });

  const selectedProduct = products.find(p => p.id === formData.product_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/inventory/add`, {
        product_id: formData.product_id, quantity: parseInt(formData.quantity),
        cost_price: parseFloat(formData.cost_price), tax_paid: parseFloat(formData.tax_paid || 0),
        supplier_name: formData.supplier_name,
        reference_number: formData.reference_number, notes: formData.notes
      }, { withCredentials: true });
      setSuccess(true);
      toast.success(t('stockAdded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stock');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="stock-added-success">
        <div className="w-24 h-24 bg-[var(--primary)]/20 mx-auto mb-6 flex items-center justify-center rounded-full">
          <CheckCircle size={60} className="text-[var(--primary)]" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--primary)] mb-2" style={{ fontFamily: 'Outfit' }}>{t('stockAdded')}</h2>
        <p className="text-[var(--dm-text-muted)] mb-8">{formData.quantity} units added to {selectedProduct?.name}</p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => { setSuccess(false); setFormData({ product_id: '', quantity: '', cost_price: '', tax_paid: '', supplier_name: '', reference_number: '', notes: '' }); }}
            data-testid="add-more-btn" className="h-12 px-8 bg-black text-white font-semibold rounded-xl border border-[var(--dm-border)]">
            {t('addMore')}
          </Button>
          <Button onClick={() => navigate('/')} variant="outline" className="h-12 px-8 border-[var(--dm-border)] text-[var(--dm-text-muted)] rounded-xl">
            {t('backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl">
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('product')} *</Label>
        <Select value={formData.product_id} onValueChange={(v) => {
          const prod = products.find(p => p.id === v);
          setFormData({ ...formData, product_id: v, cost_price: prod?.cost_price?.toString() || '' });
        }}>
          <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" data-testid="product-select">
            <SelectValue placeholder={t('selectProduct')} />
          </SelectTrigger>
          <SelectContent className="bg-[var(--card)] border-[var(--dm-border)] max-h-64">
            {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.product_code} - {p.name}</SelectItem>))}
          </SelectContent>
        </Select>
        {selectedProduct && (
          <p className="text-xs text-[var(--dm-text-muted)] mt-2">{t('currentStock')}: <span className="text-[var(--primary)] font-mono">{selectedProduct.current_stock}</span></p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('quantity')} *</Label>
          <Input type="number" min="1" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
            required data-testid="quantity-input" placeholder="Enter quantity"
            className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 text-lg font-mono" />
        </div>
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('costPrice')} *</Label>
          <Input type="number" step="0.01" min="0" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })}
            required data-testid="cost-input" placeholder="0.00"
            className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 text-lg font-mono" />
        </div>
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('taxPaid')}</Label>
        <Input type="number" step="0.01" min="0" value={formData.tax_paid} onChange={(e) => setFormData({ ...formData, tax_paid: e.target.value })}
          data-testid="tax-paid-input" placeholder="Tax amount paid on this purchase"
          className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 text-lg font-mono" />
        <p className="text-xs text-[var(--dm-text-muted)] mt-1">{t('taxPaidHint')}</p>
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('supplierName')}</Label>
        <Input value={formData.supplier_name} onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
          data-testid="supplier-input" placeholder="Enter supplier name"
          className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('referenceNumber')}</Label>
        <Input value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
          data-testid="reference-input" placeholder="PO number, invoice, etc."
          className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" />
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('notes')}</Label>
        <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          data-testid="notes-input" placeholder="Optional notes..."
          className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl resize-none" rows={3} />
      </div>
      <Button type="submit" disabled={saving || !formData.product_id || !formData.quantity || !formData.cost_price}
        data-testid="save-stock-btn" className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50 border border-[var(--dm-border)]">
        {saving ? <CircleNotch size={24} className="animate-spin" /> : t('addStockTitle')}
      </Button>
    </form>
  );
}

function UploadStockForm({ navigate, t }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inventory_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/upload`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      if (res.data.success > 0) toast.success(`Successfully uploaded ${res.data.success} items!`);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl" data-testid="upload-stock-section">
      <p className="text-[var(--dm-text-muted)] text-center">{t('bulkUploadCSV')}</p>
      <Button type="button" variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn"
        className="w-full h-12 border-[var(--dm-border)] text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-xl">
        <Download size={20} className="mr-2" />
        {t('downloadTemplate')}
      </Button>
      <div className="border-t border-[var(--dm-border)]" />
      <form onSubmit={handleUpload} className="space-y-4">
        <div>
          <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('uploadCSVFile')}</Label>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input"
            className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 file:bg-black file:text-[var(--text)] file:border-0 file:mr-4 file:px-4 file:h-full file:rounded-l-xl" />
          {file && <p className="text-xs text-[var(--primary)] mt-2">Selected: {file.name}</p>}
        </div>
        <Button type="submit" disabled={uploading || !file} data-testid="upload-btn"
          className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50 border border-[var(--dm-border)]">
          {uploading ? <CircleNotch size={24} className="animate-spin" /> : <><FileArrowUp size={24} className="mr-2" />{t('uploadFile')}</>}
        </Button>
      </form>
      {result && (
        <div className="p-4 bg-[var(--bg)] border border-[var(--dm-border)] rounded-xl">
          <h3 className="font-semibold text-[var(--text)] mb-4" style={{ fontFamily: 'Outfit' }}>{t('uploadResults')}</h3>
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle size={24} className="text-[var(--primary)]" />
            <span className="text-[var(--primary)]">{result.success} {t('itemsUploaded')}</span>
          </div>
          {result.errors?.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <XCircle size={24} className="text-[#EF4444]" />
                <span className="text-[#EF4444]">{result.errors.length} {t('errors')}</span>
              </div>
              <div className="max-h-40 overflow-y-auto text-xs text-[var(--dm-text-muted)] bg-[var(--bg)] p-2 border border-[var(--dm-border)] rounded-lg">
                {result.errors.map((err, i) => (<p key={i} className="mb-1">Row {err.row}: {err.error}</p>))}
              </div>
            </div>
          )}
          <Button onClick={() => { setFile(null); setResult(null); }} className="mt-4 w-full h-10 bg-black text-white rounded-xl border border-[var(--dm-border)]">
            {t('uploadAnother')}
          </Button>
        </div>
      )}
    </div>
  );
}

function AdjustStockForm({ products, loading, navigate, t }) {
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resultStock, setResultStock] = useState(0);
  const [formData, setFormData] = useState({ product_id: '', adjustment_quantity: '', reason: '', notes: '' });

  const selectedProduct = products.find(p => p.id === formData.product_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/inventory/adjustment`, {
        product_id: formData.product_id, adjustment_quantity: parseInt(formData.adjustment_quantity),
        reason: formData.reason, notes: formData.notes
      }, { withCredentials: true });
      setResultStock(res.data.new_stock);
      setSuccess(true);
      toast.success(t('stockAdjusted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12" data-testid="adjust-success">
        <div className="w-24 h-24 bg-[var(--primary)]/20 mx-auto mb-6 flex items-center justify-center rounded-full">
          <CheckCircle size={60} className="text-[var(--primary)]" />
        </div>
        <h2 className="text-2xl font-bold text-[var(--primary)] mb-2" style={{ fontFamily: 'Outfit' }}>{t('stockAdjusted')}</h2>
        <p className="text-[var(--dm-text-muted)] mb-2">{selectedProduct?.name}</p>
        <p className="text-2xl font-mono text-[var(--text)] mb-8">{t('newStock')}: <span className="text-[var(--primary)]">{resultStock}</span></p>
        <div className="flex gap-4 justify-center">
          <Button onClick={() => { setSuccess(false); setFormData({ product_id: '', adjustment_quantity: '', reason: '', notes: '' }); }}
            data-testid="adjust-more-btn" className="h-12 px-8 bg-black text-white font-semibold rounded-xl border border-[var(--dm-border)]">
            {t('adjustMore')}
          </Button>
          <Button onClick={() => navigate('/')} variant="outline" className="h-12 px-8 border-[var(--dm-border)] text-[var(--dm-text-muted)] rounded-xl">
            {t('backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-[var(--card)] border border-[var(--dm-border)] p-6 rounded-xl" data-testid="adjust-stock-section">
      <p className="text-[var(--dm-text-muted)] text-center">{t('correctLevels')}</p>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('product')} *</Label>
        <Select value={formData.product_id} onValueChange={(v) => setFormData({ ...formData, product_id: v })}>
          <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" data-testid="adjust-product-select">
            <SelectValue placeholder={t('selectProduct')} />
          </SelectTrigger>
          <SelectContent className="bg-[var(--card)] border-[var(--dm-border)] max-h-64">
            {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.product_code} - {p.name}</SelectItem>))}
          </SelectContent>
        </Select>
        {selectedProduct && (
          <p className="text-xs text-[var(--dm-text-muted)] mt-2">{t('currentStock')}: <span className="text-[var(--primary)] font-mono">{selectedProduct.current_stock}</span></p>
        )}
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('adjustmentQuantity')} *</Label>
        <Input type="number" value={formData.adjustment_quantity} onChange={(e) => setFormData({ ...formData, adjustment_quantity: e.target.value })}
          required data-testid="adjustment-input" placeholder="Use negative to decrease (e.g., -5)"
          className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12 text-lg font-mono" />
        <p className="text-xs text-[var(--dm-text-muted)] mt-1">{t('enterPositiveNegative')}</p>
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('reason')} *</Label>
        <Select value={formData.reason} onValueChange={(v) => setFormData({ ...formData, reason: v })}>
          <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl h-12" data-testid="reason-select">
            <SelectValue placeholder={t('selectReason')} />
          </SelectTrigger>
          <SelectContent className="bg-[var(--card)] border-[var(--dm-border)]">
            <SelectItem value="damaged">{t('damaged')}</SelectItem>
            <SelectItem value="lost">{t('lost')}</SelectItem>
            <SelectItem value="found">{t('foundStock')}</SelectItem>
            <SelectItem value="manual_correction">{t('manualCorrection')}</SelectItem>
            <SelectItem value="other">{t('other')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[var(--dm-text-muted)] text-sm mb-2 block">{t('notes')}</Label>
        <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          data-testid="adjust-notes-input" placeholder="Optional notes..."
          className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-xl resize-none" rows={3} />
      </div>
      <Button type="submit" disabled={saving || !formData.product_id || !formData.adjustment_quantity || !formData.reason}
        data-testid="save-adjustment-btn" className="w-full h-14 bg-black text-white font-bold text-lg rounded-xl hover:bg-black/80 disabled:opacity-50 border border-[var(--dm-border)]">
        {saving ? <CircleNotch size={24} className="animate-spin" /> : t('adjustStock')}
      </Button>
    </form>
  );
}
